import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { FiArrowRight, FiSearch } from 'react-icons/fi';
import { questionsApi } from '@/services/questions';
import usePermission from '@/hooks/usePermission';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { INITIAL_FILTERS, PRESETS } from './filters';

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

const DISPONIBILIDADE_OPTIONS = [
  { value: 'true', label: 'Inéditas' },
  { value: 'false', label: 'Já usadas' }
];

const USADAS_EM_OPTIONS = [
  { value: 'exam', label: 'Provas' },
  { value: 'train', label: 'Treino/Simulado' },
  { value: 'open', label: 'Aberto/Exercício' }
];

const ETAPA_OPTIONS = [
  { value: 'serie_3_em', label: '3º EM' },
  { value: 'ano_9_ef', label: '9º EF' },
  { value: 'ano_5_ef', label: '5º EF' },
  { value: 'ano_2_ef', label: '2º EF' }
];

export function QuestionsGateTeacher({ taxonomies = [], filters, onFiltersChange, onApply }) {
  const navigate = useNavigate();
  const client = useClient();
  const canCreateQuestion = usePermission('Criar questões', 'Questões e exercícios');

  const [materiaSearch, setMateriaSearch] = useState('');
  const [descritores, setDescritores] = useState([]);
  const [count, setCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(false);
  const debounceRef = useRef(null);

  // Contagem live via API, com debounce — mesma lógica de QuestionsGate.jsx (default).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoadingCount(true);
    debounceRef.current = setTimeout(() => {
      questionsApi
        .count(filters)
        .then(json => setCount(json?.count ?? 0))
        .catch(() => setCount(null))
        .finally(() => setLoadingCount(false));
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [filters]);

  // Descritores da(s) matéria(s) selecionada(s) — mesma lógica de QuestionsFilters.jsx (default).
  useEffect(() => {
    const materiaIds = filters['taxonomies.id'] || [];
    if (materiaIds.length === 0) {
      setDescritores([]);
      return;
    }
    Promise.all(
      materiaIds.map(id =>
        client
          .fetch(`taxonomies/used_by_questions?parent_id=${id}`)
          .then(r => r?.data || [])
          .catch(() => [])
      )
    ).then(results => setDescritores(results.flat()));
  }, [filters['taxonomies.id']]); // eslint-disable-line react-hooks/exhaustive-deps

  // Remove de descritor_ids qualquer id que não pertença mais às matérias selecionadas.
  useEffect(() => {
    const validIds = new Set(descritores.map(d => d.id));
    const current = filters.descritor_ids || [];
    const pruned = current.filter(id => validIds.has(id));
    if (pruned.length !== current.length) {
      onFiltersChange({ ...filters, descritor_ids: pruned });
    }
  }, [descritores]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleIneditas = value => {
    onFiltersChange({ ...filters, ineditas: filters.ineditas === value ? undefined : value });
  };

  const toggleValue = (key, value) => {
    const current = filters[key] || [];
    const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
    onFiltersChange({ ...filters, [key]: next });
  };

  const toggleMateria = materiaId => {
    const current = filters['taxonomies.id'] || [];
    const next = current.includes(materiaId) ? current.filter(id => id !== materiaId) : [...current, materiaId];
    onFiltersChange({ ...filters, 'taxonomies.id': next });
  };

  const applyPreset = preset => onFiltersChange({ ...INITIAL_FILTERS, ...preset.filters });

  const isPresetActive = preset => {
    const target = { ...INITIAL_FILTERS, ...preset.filters };
    return Object.keys(INITIAL_FILTERS).every(key => {
      const expected = target[key];
      if (Array.isArray(expected)) {
        const current = [...(filters[key] || [])].sort();
        const expectedSorted = [...expected].sort();
        return current.length === expectedSorted.length && current.every((value, index) => value === expectedSorted[index]);
      }
      return (filters[key] ?? undefined) === (expected ?? undefined);
    });
  };

  const visibleMaterias = taxonomies.filter(tax => tax.name.toLowerCase().includes(materiaSearch.toLowerCase()));

  const canApply = count !== null && count > 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        {canCreateQuestion && (
          <PlayfulCardButton
            className="w-auto px-6"
            onClick={() => navigate('/questoes/criar/nome')}
          >
            Nova questão manual
          </PlayfulCardButton>
        )}
      </div>

      <div className="rounded-2xl border border-neutral-100 bg-white p-5 shadow-card">
        <h3 className="text-xs font-bold uppercase tracking-wide text-neutral-600">Atalhos</h3>
        <div className="mt-2.5 flex flex-wrap gap-2">
          {PRESETS.map(preset => (
            <Chip
              key={preset.id}
              active={isPresetActive(preset)}
              onClick={() => applyPreset(preset)}
              data-tooltip-id="preset-tooltip"
              data-tooltip-content={preset.description}
            >
              {preset.label}
            </Chip>
          ))}
        </div>
        <ReactTooltip
          id="preset-tooltip"
          className="tooltip"
          place="top"
        />
      </div>

      <div className="rounded-2xl border border-neutral-100 bg-white p-5 shadow-card">
        <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
          <FilterGroup label="Uso da questão">
            {DISPONIBILIDADE_OPTIONS.map(opt => (
              <Chip
                key={opt.value}
                active={filters.ineditas === opt.value}
                onClick={() => toggleIneditas(opt.value)}
              >
                {opt.label}
              </Chip>
            ))}
          </FilterGroup>

          <FilterGroup label="Usadas em qual categoria">
            {USADAS_EM_OPTIONS.map(opt => (
              <Chip
                key={opt.value}
                active={(filters.usadas_em || []).includes(opt.value)}
                onClick={() => toggleValue('usadas_em', opt.value)}
              >
                {opt.label}
              </Chip>
            ))}
          </FilterGroup>

          <FilterGroup label="Série">
            {ETAPA_OPTIONS.map(opt => (
              <Chip
                key={opt.value}
                active={(filters.etapa || []).includes(opt.value)}
                onClick={() => toggleValue('etapa', opt.value)}
              >
                {opt.label}
              </Chip>
            ))}
          </FilterGroup>
        </div>

        <div className="mt-6">
          <label
            htmlFor="materiaSearch"
            className="text-xs font-bold uppercase tracking-wide text-neutral-600"
          >
            Matéria
          </label>
          <div className="relative mt-2.5 max-w-sm">
            <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
            <input
              id="materiaSearch"
              type="search"
              placeholder="Buscar matéria"
              value={materiaSearch}
              onChange={e => setMateriaSearch(e.target.value)}
              className="w-full rounded-full border border-neutral-100 py-2 pl-9 pr-3 text-sm outline-none focus:border-[var(--color-mission)]"
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {visibleMaterias.map(tax => (
              <Chip
                key={tax.id}
                active={(filters['taxonomies.id'] || []).includes(tax.id)}
                onClick={() => toggleMateria(tax.id)}
              >
                {tax.name}
              </Chip>
            ))}
          </div>
        </div>

        {descritores.length > 0 && (
          <div className="mt-6">
            <FilterGroup label="Descritores">
              {descritores.map(descritor => (
                <Chip
                  key={descritor.id}
                  active={(filters.descritor_ids || []).includes(descritor.id)}
                  onClick={() => toggleValue('descritor_ids', descritor.id)}
                >
                  {descritor.name}
                </Chip>
              ))}
            </FilterGroup>
          </div>
        )}
      </div>

      <div
        className="flex flex-col items-start justify-between gap-4 rounded-2xl border border-neutral-100 p-5 shadow-card sm:flex-row sm:items-center"
        style={{ background: 'linear-gradient(180deg, #F4FDFF 0%, #F2FCFF 14.65%, #FFFCF1 100%)' }}
      >
        <CountIndicator
          count={count}
          loading={loadingCount}
        />
        <PlayfulCardButton
          className="w-auto px-6"
          variant="rose"
          icon={FiArrowRight}
          disabled={!canApply}
          onClick={onApply}
        >
          Ver questões encontradas
        </PlayfulCardButton>
      </div>
    </div>
  );
}

function CountIndicator({ count, loading }) {
  if (loading && count === null) {
    return (
      <div className="flex items-center gap-3">
        <span className="inline-block h-4 w-24 animate-pulse rounded-full bg-neutral-100" />
        <span className="text-sm font-semibold text-neutral-600">contando questões…</span>
      </div>
    );
  }

  if (count === null) {
    return <span className="text-sm text-neutral-500">—</span>;
  }

  return (
    <span
      className="text-lg font-extrabold"
      style={{ color: '#222731' }}
    >
      {count} {count === 1 ? 'questão cadastrada' : 'questões cadastradas'}
      {loading && <span className="ml-2 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--color-mission)] align-middle" />}
    </span>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div>
      <span className="text-xs font-bold uppercase tracking-wide text-neutral-600">{label}</span>
      <div className="mt-2.5 flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function Chip({ active, onClick, children, ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-active={active}
      className={[
        'inline-flex select-none items-center rounded-full border px-3 py-1.5 text-sm font-semibold',
        'transition-[transform,colors,border-color,background-color] duration-200 active:scale-[0.96]',
        active ? 'border-[var(--color-mission)] bg-[var(--color-mission)] text-white shadow-[0_1px_2px_rgba(0,0,0,0.06)]' : 'hover:border-[var(--color-mission)]/40 border-neutral-100 bg-white text-neutral-700'
      ].join(' ')}
      style={{ transitionTimingFunction: EASE_OUT }}
      {...rest}
    >
      {children}
    </button>
  );
}
